import { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ReferenceLine,
} from 'recharts';
import { HL_WALLET, HL_URL } from '../hyperliquid';
import { tradeResult } from '../tradePnl';
import './Compare.css';

const STRATEGY_COLORS = [
  { dot: '#4a9eff', bg: 'rgba(74,158,255,0.13)',   border: 'rgba(74,158,255,0.32)' },
  { dot: '#00c896', bg: 'rgba(0,200,150,0.13)',    border: 'rgba(0,200,150,0.32)'  },
  { dot: '#ff6b6b', bg: 'rgba(255,107,107,0.13)',  border: 'rgba(255,107,107,0.32)' },
  { dot: '#ffd166', bg: 'rgba(255,209,102,0.13)',  border: 'rgba(255,209,102,0.32)' },
];

const TT_STYLE = {
  background: '#1a1a1e', border: '1px solid #2a2a2e',
  borderRadius: 6, fontSize: 12, color: '#e8eaf0',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function colorFor(allStrategies, name) {
  const idx = allStrategies.indexOf(name);
  return STRATEGY_COLORS[Math.max(0, idx) % STRATEGY_COLORS.length];
}

function msOf(ts) {
  if (!ts) return 0;
  return ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime();
}

function fmtDuration(ms) {
  if (!ms || ms <= 0) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s % 60}s`;
}

function fmtPrice(p) {
  if (p == null) return '—';
  if (p >= 10000) return `$${Number(p).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (p >= 1000)  return `$${Number(p).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `$${Number(p).toFixed(4)}`;
}

function fmtPnl(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (n === 0) return '$0.00';
  return `${n > 0 ? '+' : '-'}$${Math.abs(n).toFixed(4)}`;
}

function toDateInput(d) { return d.toISOString().slice(0, 10); }

// ── Data loading ──────────────────────────────────────────────────────────────
function useCompareData() {
  const [signals,     setSignals]     = useState([]);
  const [trailEvents, setTrailEvents] = useState([]);
  const [fills,       setFills]       = useState([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [sigSnap, trailSnap, fillsResp] = await Promise.all([
          getDocs(query(collection(db, 'signals'),     orderBy('timestamp', 'desc'))),
          getDocs(query(collection(db, 'trail_events'), orderBy('timestamp', 'asc'))),
          fetch(HL_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'userFills', user: HL_WALLET }),
          }).then(r => r.json()),
        ]);
        setSignals(sigSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setTrailEvents(trailSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setFills(Array.isArray(fillsResp) ? fillsResp : []);
      } catch (e) {
        console.error('[Compare] load error:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return { signals, trailEvents, fills, loading };
}

// ── Build trade records ───────────────────────────────────────────────────────
function buildRecords(signals, trailEvents, fills) {
  return signals
    .filter(s => s.action !== 'CLOSE' && s.status !== 'error' && s.status !== 'duplicate_skipped' && s.tradeId)
    .map(signal => {
      const coin      = (signal.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
      const direction = (signal.direction || '').toUpperCase();
      const signalMs  = msOf(signal.timestamp);
      const dirLabel  = direction === 'LONG' ? 'Long' : 'Short';

      // Next signal for same coin (desc-sorted → last in filtered = chronologically closest)
      const afterThis = signals.filter(s =>
        (s.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '') === coin &&
        s.id !== signal.id && msOf(s.timestamp) > signalMs &&
        s.status !== 'duplicate_skipped'
      );
      const nextMs = afterThis.length > 0 ? msOf(afterThis[afterThis.length - 1].timestamp) : null;

      // Closing fills between signal and next signal
      const closingFills = fills
        .filter(f =>
          f.coin === coin &&
          (f.dir || '').includes(dirLabel) &&
          (f.dir || '').includes('Close') &&
          f.time >= signalMs &&
          (!nextMs || f.time < nextMs)
        )
        .sort((a, b) => a.time - b.time);

      const lastClose = closingFills[closingFills.length - 1];
      // Prefer the backend's real, verified exit_price/exit_time (Aug 1 fix) over
      // re-deriving from live HL fills — the fill-window match structurally misses
      // flip-closes, since the next signal's own timestamp (nextMs) is written
      // before the old position's actual closing fill happens on Hyperliquid.
      const exitPx = signal.exit_price != null ? parseFloat(signal.exit_price)
                   : lastClose ? parseFloat(lastClose.px) : null;
      const exitMs = signal.exit_time?.toDate ? signal.exit_time.toDate().getTime()
                   : signal.exit_time ? new Date(signal.exit_time).getTime()
                   : lastClose ? lastClose.time : null;

      const entryPx  = signal.fill_price ? parseFloat(signal.fill_price) : signal.price ? parseFloat(signal.price) : null;
      const hl       = tradeResult(signal, fills, nextMs);
      const estPnl   = hl ? hl.net : null;

      // Exit reason from trail events for this trade window
      const trailForTrade = trailEvents.filter(e => {
        const et = msOf(e.timestamp);
        return (e.symbol || '') === coin &&
               (e.direction || '') === direction &&
               et >= signalMs &&
               (!nextMs || et < nextMs);
      });
      // Remove false-positive "position_closed_externally" events that aren't last
      const filteredTrail      = trailForTrade.filter((e, i) =>
        !(e.note === 'position_closed_externally' && i < trailForTrade.length - 1)
      );
      const partialEvent       = filteredTrail.find(e => (e.note || '').includes('partial') && !e.note.includes('position_closed'));
      const closedAfterPartial = filteredTrail.some(e => e.note === 'position_closed_after_partial');
      // Only trust "closed externally" if fills confirm AND no other trail events (pure manual close)
      const closedExternally   = closingFills.length > 0 &&
                                 filteredTrail.some(e => e.note === 'position_closed_externally') &&
                                 filteredTrail.length === 1;

      // Prefer the backend's real, verified exit_reason (Aug 1 fix) over the local
      // trail-event/fill heuristic below, which was never updated to know about it
      // (same gap as TradeDetailPanel.js had before that fix).
      const EXIT_REASON_LABELS = {
        sl_hit: 'SL hit',
        tp_hit: 'TP hit',
        closed_externally: 'Closed externally',
        closed_externally_after_partial: 'Closed externally (after partial)',
        flipped_by_opposite_signal: 'Closed — flipped by opposite signal',
      };
      let exitReason;
      if (signal.exit_reason) {
        exitReason = EXIT_REASON_LABELS[signal.exit_reason] || signal.exit_reason;
      } else {
        exitReason = exitPx == null ? 'Open' : '—';
        if (exitPx != null || closedAfterPartial || closedExternally) {
          if (partialEvent && closedAfterPartial) exitReason = 'Trailing stop';
          else if (partialEvent)                  exitReason = 'Trailing stop (partial)';
          else if (filteredTrail.length > 0)      exitReason = 'SL hit (trailing) (est.)';
          else if (closedExternally)              exitReason = 'Closed externally (est.)';
          else                                    exitReason = 'SL / TP hit (est.)';
        }
      }

      return {
        id:         signal.id,
        tradeId:    signal.tradeId || '—',
        strategy:   signal.strategy || signal.source || 'Unknown',
        coin,
        direction,
        entryPx,
        exitPx,
        signalMs,
        exitMs,
        duration:   signalMs && exitMs ? exitMs - signalMs : null,
        estPnl,
        exitReason,
      };
    });
}

// ── Compare page ──────────────────────────────────────────────────────────────
const TABLE_COLS = [
  { key: 'tradeId',    label: 'ID' },
  { key: 'strategy',   label: 'Strategy' },
  { key: 'direction',  label: 'Side' },
  { key: 'entryPx',   label: 'Entry' },
  { key: 'exitPx',    label: 'Exit' },
  { key: 'duration',  label: 'Duration' },
  { key: 'estPnl',    label: 'Est. PnL' },
  { key: 'exitReason', label: 'Exit Reason' },
];

function Compare() {
  const { signals, trailEvents, fills, loading } = useCompareData();

  const [coinFilter,         setCoinFilter]         = useState('ALL');
  const [sideFilter,         setSideFilter]         = useState('ALL');
  const [selectedStrategies, setSelectedStrategies] = useState(null); // null = all
  const [dateFrom,           setDateFrom]           = useState('');
  const [dateTo,             setDateTo]             = useState('');
  const [sortCol,            setSortCol]            = useState('signalMs');
  const [sortDir,            setSortDir]            = useState('desc');

  const allRecords = useMemo(
    () => buildRecords(signals, trailEvents, fills),
    [signals, trailEvents, fills]
  );

  const allStrategies = useMemo(() => {
    const s = new Set(allRecords.map(r => r.strategy));
    return [...s].sort();
  }, [allRecords]);

  const allCoins = useMemo(() => {
    const s = new Set(allRecords.map(r => r.coin).filter(Boolean));
    return ['ALL', ...[...s].sort()];
  }, [allRecords]);

  // Default to all strategies selected
  const activeStrategies = selectedStrategies ?? allStrategies;

  function toggleStrategy(s) {
    const current = selectedStrategies ?? allStrategies;
    setSelectedStrategies(
      current.includes(s) ? current.filter(x => x !== s) : [...current, s]
    );
  }

  // Filter records
  const filtered = useMemo(() => allRecords.filter(r => {
    if (coinFilter !== 'ALL' && r.coin !== coinFilter)                       return false;
    if (sideFilter !== 'ALL' && r.direction !== sideFilter)                  return false;
    if (!activeStrategies.includes(r.strategy))                              return false;
    if (dateFrom && new Date(r.signalMs) < new Date(dateFrom))               return false;
    if (dateTo   && new Date(r.signalMs) > new Date(dateTo + 'T23:59:59'))   return false;
    return true;
  }), [allRecords, coinFilter, sideFilter, activeStrategies, dateFrom, dateTo]);

  // Sort records
  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return dir * av.localeCompare(bv);
      return dir * (av - bv);
    });
  }, [filtered, sortCol, sortDir]);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }

  // Stats per active strategy
  const stats = useMemo(() => activeStrategies.map(s => {
    const trades  = filtered.filter(r => r.strategy === s);
    const closed  = trades.filter(r => r.estPnl != null);
    const wins    = closed.filter(r => r.estPnl > 0);
    const totalPnl = closed.reduce((sum, r) => sum + r.estPnl, 0);
    const durs    = closed.filter(r => r.duration > 0).map(r => r.duration);
    return {
      strategy:    s,
      total:       trades.length,
      winRate:     closed.length > 0 ? wins.length / closed.length * 100 : null,
      totalPnl,
      avgDuration: durs.length > 0 ? durs.reduce((a, b) => a + b, 0) / durs.length : null,
    };
  }), [filtered, activeStrategies]);

  // Cumulative PnL chart — per strategy, indexed by trade count
  const cumPnLData = useMemo(() => {
    const byStrategy = {};
    activeStrategies.forEach(s => { byStrategy[s] = []; });

    filtered
      .filter(r => r.estPnl != null && r.strategy in byStrategy)
      .sort((a, b) => a.signalMs - b.signalMs)
      .forEach(r => {
        const arr = byStrategy[r.strategy];
        const prev = arr.length > 0 ? arr[arr.length - 1] : 0;
        arr.push(+(prev + r.estPnl).toFixed(4));
      });

    const maxLen = Math.max(...Object.values(byStrategy).map(a => a.length), 0);
    if (maxLen === 0) return [];

    return Array.from({ length: maxLen }, (_, i) => {
      const pt = { trade: i + 1 };
      activeStrategies.forEach(s => { pt[s] = byStrategy[s]?.[i] ?? null; });
      return pt;
    });
  }, [filtered, activeStrategies]);

  // Win rate by side chart
  const winRateData = useMemo(() => activeStrategies.map(s => {
    const trades = filtered.filter(r => r.strategy === s && r.estPnl != null);
    const longs  = trades.filter(r => r.direction === 'LONG');
    const shorts = trades.filter(r => r.direction === 'SHORT');
    const abbrev = s.length > 16 ? s.slice(0, 14) + '…' : s;
    return {
      name:  abbrev,
      LONG:  longs.length  > 0 ? +(longs.filter(r => r.estPnl  > 0).length / longs.length  * 100).toFixed(1) : 0,
      SHORT: shorts.length > 0 ? +(shorts.filter(r => r.estPnl > 0).length / shorts.length * 100).toFixed(1) : 0,
    };
  }), [filtered, activeStrategies]);

  const hasFilter = coinFilter !== 'ALL' || sideFilter !== 'ALL' || dateFrom || dateTo;

  function clearFilters() {
    setCoinFilter('ALL');
    setSideFilter('ALL');
    setDateFrom('');
    setDateTo('');
    setSelectedStrategies(null);
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <p>Loading comparison data…</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Compare</h1>
        <p>Strategy performance comparison · {filtered.length} trade{filtered.length !== 1 ? 's' : ''}</p>
      </div>

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <div className="cmp-toolbar card">
        {/* Row 1: Side, Coin, Dates, Count */}
        <div className="cmp-toolbar-row">
          <div className="cmp-filter-group">
            <label className="cmp-filter-label">Side</label>
            <div className="cmp-side-btns">
              {['ALL', 'LONG', 'SHORT'].map(side => (
                <button key={side}
                  className={`cmp-side-filter-btn cmp-side-filter-btn--${side.toLowerCase()}${sideFilter === side ? ' active' : ''}`}
                  onClick={() => setSideFilter(side)}>
                  {side}
                </button>
              ))}
            </div>
          </div>

          <div className="cmp-filter-group">
            <label className="cmp-filter-label">Coin</label>
            <select className="cmp-filter-select" value={coinFilter} onChange={e => setCoinFilter(e.target.value)}>
              {allCoins.map(c => <option key={c} value={c}>{c === 'ALL' ? 'All Coins' : c}</option>)}
            </select>
          </div>

          <div className="cmp-filter-group">
            <label className="cmp-filter-label">From</label>
            <input type="date" className="cmp-filter-select" value={dateFrom}
              max={dateTo || toDateInput(new Date())} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div className="cmp-filter-group">
            <label className="cmp-filter-label">To</label>
            <input type="date" className="cmp-filter-select" value={dateTo}
              min={dateFrom} max={toDateInput(new Date())} onChange={e => setDateTo(e.target.value)} />
          </div>

          <div className="cmp-toolbar-right">
            {(hasFilter || selectedStrategies !== null) && (
              <button className="cmp-clear-btn" onClick={clearFilters}>Clear</button>
            )}
            <span className="cmp-count">{filtered.length} trade{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Row 2: Strategy toggles */}
        <div className="cmp-toolbar-row cmp-toolbar-row--strategies">
          <div className="cmp-filter-group cmp-filter-group--full">
            <label className="cmp-filter-label">Strategy</label>
            <div className="cmp-strategy-btns">
              {allStrategies.map(s => {
                const c      = colorFor(allStrategies, s);
                const active = activeStrategies.includes(s);
                return (
                  <button key={s}
                    className={`cmp-strategy-btn${active ? ' cmp-strategy-btn--active' : ''}`}
                    style={active ? { background: c.bg, borderColor: c.border, color: c.dot } : {}}
                    onClick={() => toggleStrategy(s)}>
                    <span className="cmp-dot" style={{ background: active ? c.dot : 'var(--text-muted)' }} />
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats grid ───────────────────────────────────────────────────── */}
      <div className="cmp-stats-grid">
        {[
          {
            label: 'Win Rate',
            render: st => {
              const v = st.winRate;
              const color = v == null ? undefined : v >= 50 ? 'var(--green)' : 'var(--red)';
              return { text: v != null ? `${v.toFixed(1)}%` : 'N/A', color };
            },
          },
          {
            label: 'Total PnL',
            render: st => {
              const v = st.totalPnl;
              return { text: fmtPnl(v), color: v > 0 ? 'var(--green)' : v < 0 ? 'var(--red)' : undefined };
            },
          },
          {
            label: 'Avg Duration',
            render: st => ({ text: st.avgDuration ? fmtDuration(st.avgDuration) : 'N/A', color: undefined }),
          },
          {
            label: 'Total Trades',
            render: st => ({ text: String(st.total), color: undefined }),
          },
        ].map(({ label, render }) => (
          <div key={label} className="card cmp-stat-card">
            <div className="cmp-stat-label">{label}</div>
            {stats.map(st => {
              const c = colorFor(allStrategies, st.strategy);
              const { text, color } = render(st);
              return (
                <div key={st.strategy} className="cmp-stat-row">
                  <span className="cmp-dot" style={{ background: c.dot }} />
                  <span className="cmp-stat-name">
                    {st.strategy.length > 20 ? st.strategy.slice(0, 18) + '…' : st.strategy}
                  </span>
                  <span className="cmp-stat-value" style={color ? { color } : undefined}>{text}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* ── Charts ───────────────────────────────────────────────────────── */}
      <div className="cmp-charts-row">
        <div className="card cmp-chart-card">
          <div className="cmp-chart-title">Cumulative PnL</div>
          {cumPnLData.length > 0 ? (
            <ResponsiveContainer width="100%" height={210}>
              <LineChart data={cumPnLData} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
                <XAxis dataKey="trade" tick={{ fill: '#8b8b9a', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8b8b9a', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={TT_STYLE} formatter={(v, name) => [fmtPnl(v), name]} />
                <ReferenceLine y={0} stroke="#2a2a2e" />
                {activeStrategies.map(s => (
                  <Line key={s} type="monotone" dataKey={s}
                    stroke={colorFor(allStrategies, s).dot} strokeWidth={2}
                    dot={false} connectNulls
                    activeDot={{ r: 4, fill: colorFor(allStrategies, s).dot }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="cmp-chart-empty">No closed trades yet</div>
          )}
        </div>

        <div className="card cmp-chart-card">
          <div className="cmp-chart-title">Win Rate by Side</div>
          {winRateData.some(d => d.LONG > 0 || d.SHORT > 0) ? (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={winRateData} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fill: '#8b8b9a', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8b8b9a', fontSize: 10 }} axisLine={false} tickLine={false}
                  tickFormatter={v => `${v}%`} domain={[0, 100]} />
                <Tooltip contentStyle={TT_STYLE} formatter={(v, name) => [`${v}%`, name]} />
                <Bar dataKey="LONG"  fill="var(--green)" fillOpacity={0.82} radius={[3,3,0,0]} />
                <Bar dataKey="SHORT" fill="var(--red)"   fillOpacity={0.82} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="cmp-chart-empty">No closed trades yet</div>
          )}
        </div>
      </div>

      {/* ── Trade table ──────────────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 16, overflowX: 'auto' }}>
        {sorted.length === 0 ? (
          <div className="empty-state">
            <span className="icon">⇄</span>
            <p>No trades match the selected filters.</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                {TABLE_COLS.map(({ key, label }) => (
                  <th key={key} className="cmp-th" onClick={() => handleSort(key)}>
                    {label}
                    <span className={`cmp-sort-icon${sortCol === key ? ' cmp-sort-icon--active' : ''}`}>
                      {sortCol === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                const c = colorFor(allStrategies, r.strategy);
                const pnlColor = r.estPnl == null ? undefined
                  : r.estPnl > 0 ? 'var(--green)' : 'var(--red)';
                return (
                  <tr key={r.id}>
                    <td className="cmp-trade-id">{r.tradeId}</td>
                    <td>
                      <span className="cmp-strategy-badge"
                        style={{ background: c.bg, borderColor: c.border, color: c.dot }}>
                        <span className="cmp-dot" style={{ background: c.dot }} />
                        {r.strategy}
                      </span>
                    </td>
                    <td>
                      <span className={`cmp-side-badge cmp-side-badge--${r.direction.toLowerCase()}`}>
                        {r.direction}
                      </span>
                    </td>
                    <td>{fmtPrice(r.entryPx)}</td>
                    <td>{fmtPrice(r.exitPx)}</td>
                    <td className="cmp-muted">{fmtDuration(r.duration)}</td>
                    <td style={{ color: pnlColor, fontWeight: r.estPnl != null ? 700 : undefined }}>
                      {fmtPnl(r.estPnl)}
                    </td>
                    <td className="cmp-muted">{r.exitReason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Compare;
